import { FC, Fragment, useEffect, useState } from 'react';
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Col,
	Collapse,
	Divider,
	Form,
	Input,
	InputNumber,
	Result,
	Row,
	Select,
	Table,
} from 'antd';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getCustomerDetail,
	getCustomerPlafons,
} from '@/libs/fleet-api/services/customer';
import { useApp } from '@/App';
import FleetModal from '@/libs/antd/components/Modal';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import { useForm } from '@/libs/antd/form';
import {
	OrderType,
	OrderTypeExisting,
	PlafondType,
	SegmentMarket,
	SourceOrder,
	getEnumMapping,
} from '@/libs/fleet-api/enums';
import {
	getPlafonSummary,
	ProspectPlafon,
	submitProspect,
	type SummaryFinancingLimitDetail,
	type SummaryGroupPlafonDetail,
} from '@/libs/fleet-api/services/prospect';
import {
	useQueryFinancingObject,
	useQueryFinancingType,
	useSelectCommercialBank,
	useSelectCorporateBank,
	// useSelectFinancingType,
	useSelectFinancingLimitCharacteristic,
	useSelectGeneralPosition,
	useSelectMandiriBranches,
	useSelectPlafonType,
	useSelectSegmentMarket,
	useSelectSourceOrder,
	useSelectSupplierDealer,
} from '@/libs/fleet-api/hooks/master-data';
import { useModals } from '@/components/modals/ModalProvider';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { inputCurrency } from '@/libs/antd/input-number';
import { formatCurrency } from '@/libs/antd/formater';
import { ArrowRightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import fetcher from '@/libs/fleet-api/fetcher';
import { ExistingFinancingLimitInformation } from '@/libs/fleet-api/services/types';
import {
	getMasterData,
	MasterData,
} from '@/libs/fleet-api/services/master-data';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type ProspectPageProps = {
	customerId: string;
	index: number;
};

type PlafonModalState =
	| {
		type: 'edit';
		index: number;
	}
	| {
		type: 'add';
	}
	| {
		type: 'existing';
		data: ExistingFinancingLimitInformation;
		index?: number;
	}
	| null;

type ProspectPlafonData = ProspectPlafon & {
	referrerUnit: MasterData;
	segmentMarketValue: string;
	orderTypeValue: string;
	plafonTypeValue: string;
	financingTypeId: number;
	financingTypeParents: number[];
	financingTypeItems: number[];
};

type PlafonForm = {
	data: ProspectPlafonData[];
	form: ProspectPlafonData;
};

type FinancingTypeDetailItem = {
	data: MasterData;
	parentId: number;
};

const ProspectPage: FC<ProspectPageProps> = ({ customerId }) => {
	const { navigate, notification } = useApp();
	const {
		openDocumentModal,
		openCustomerDetail,
		openCustomerGroupDetail,
	} = useModals();
	const queryCustomerDetail = useQuery({
		queryKey: ['getCustomerDetail', customerId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getCustomerDetail(id);
		},
		enabled: Boolean(customerId),
	});

	const queryCustomerPlafons = useQuery({
		queryKey: ['getCustomerPlafons', customerId],
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getCustomerPlafons(id);
		},
		enabled: Boolean(customerId),
	});

	const {
		customerInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
	} = queryCustomerDetail.data?.data ?? {};

	const customerType = customerInformation?.customerType?.id ?? 1;
	const isJfCustomer = customerType === 1;
	const existingPlafons = queryCustomerPlafons?.data?.data ?? [];

	const { form, value, fieldName } = useForm<PlafonForm>();

	const [successModal, setSuccessModal] = useState('');
	const [plafonModal, setPlafonModal] = useState<PlafonModalState>(null);

	const selectSegmentMarket = useSelectSegmentMarket();
	const selectSourceOrder = useSelectSourceOrder(value?.form?.segmentMarketId);
	const selectCommercialBank = useSelectCommercialBank();
	const selectSupplierDealer = useSelectSupplierDealer();
	const selectCorporateBank = useSelectCorporateBank();
	const selectMandiriBranches = useSelectMandiriBranches();
	const selectGeneralPosition = useSelectGeneralPosition();
	const selectPlafonType = useSelectPlafonType();
	const selectFinancingLimitCharacteristic = useSelectFinancingLimitCharacteristic();
	// const queryFinancingTypeDetails = useQueryFinancingTypeDetails(
	// 	value?.form?.financingTypeId,
	// );
	const queryFinancingObject = useQueryFinancingObject();
	const queryFinancingType = useQueryFinancingType();

	const queryPlafonSummary = useQuery({
		queryKey: [
			'getPlafonSummary',
			customerId,
			JSON.stringify(value?.data),
		] as const,
		queryFn: () =>
			getPlafonSummary({
				customerId,
				requestedFinancingLimitRequests: (value?.data ?? []).map(
					(plafon) => ({
						financingLimitAmount: plafon.applicationLimit,
						plafonType: plafon.plafonTypeValue,
						financingLimitType:
							(selectFinancingLimitCharacteristic.options?.find(
								(opt) => opt.value === plafon.financingLimitTypeId,
							)?.label as string) ?? null,
						expiredDate: null,
					}),
				),
			}),
		enabled: Boolean(customerId),
	});
	// const selectFinancingType = useSelectFinancingType();

	const [financingTypeDetailItems, setFinancingTypeDetailItems] =
		useState<FinancingTypeDetailItem[]>([]);

	useEffect(() => {
		const fetchAllDetails = async () => {
			const res = await getMasterData('financing-type-detail');
			const items: FinancingTypeDetailItem[] = res.data.map((detail) => ({
				data: detail,
				parentId: 0,
			}));
			setFinancingTypeDetailItems(items);
		};
		fetchAllDetails();
	}, []);

	const prospectMutation = useMutation({ mutationFn: submitProspect });

	const handleSubmitProspect = async () => {
		const { data } = form.getFieldsValue();
		try {
			const res = await prospectMutation.mutateAsync({
				customerId,
				plafons: data.map((plafon) => {
					const { referrerUnit, ...payload } = plafon;
					if (referrerUnit) payload.referrerUnitId = referrerUnit.id;
					return payload;
				}),
			});
			setSuccessModal(res?.data);
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	const handleSubmitPlafon = async (values: PlafonForm) => {
		const data = values.data || [];
		if (
			plafonModal?.type == 'edit' ||
			(plafonModal?.type == 'existing' && plafonModal?.index !== undefined)
		) {
			form?.setFieldsValue({
				data: data.map((list, index) => {
					return index === plafonModal.index ? value!.form : list;
				}) as ProspectPlafonData[],
			});
		} else {
			form?.setFieldsValue({
				data: [...data, values.form],
			});
		}
		form?.resetFields(['form']);
		setPlafonModal(null);
	};


	const getSelectOrderSource = () => {
		if (!selectSourceOrder.options?.length) return [];

		if (value?.form?.segmentMarketId === SegmentMarket.Reguler) {
			return selectSourceOrder.options;
		}

		if (value?.form?.plafonTypeValue === 'Join Financing') {
			return selectSourceOrder.options;
		} else {
			return selectSourceOrder.options;
		}
	};

	const getSelectSegmentMarket = (financingLimitType: string) => {
		if (!selectSegmentMarket.options?.length) return [];

		if (financingLimitType === 'Non Join Financing') {
			return selectSegmentMarket.options;
		} else {
			return selectSegmentMarket.options.filter(
				(option) => option.value !== SegmentMarket.Reguler,
			);
		}
	};

	const hasJF = Boolean(
		value?.data?.filter(
			(plafond) => plafond.plafonTypeId === PlafondType['Join Financing'],
		).length,
	);

	const hasNJF = Boolean(
		value?.data?.filter(
			(plafond) => plafond.plafonTypeId === PlafondType['Non Join Financing'],
		).length,
	);

	const getSelectExistingPlafonTypeOptions = (financingLimitType: string) => {
		const NJFOptionOnly = [
			{
				label: PlafondType[1],
				value: PlafondType['Non Join Financing'],
				data: {
					id: PlafondType['Non Join Financing'],
					value: PlafondType[1],
				},
			},
		];

		const JFOptionOnly = [
			{
				label: PlafondType[2],
				value: PlafondType['Join Financing'],
				data: {
					id: PlafondType['Join Financing'],
					value: PlafondType[2],
				},
			},
		];

		if (financingLimitType === 'Non Join Financing') {
			return NJFOptionOnly;
		}
		return JFOptionOnly;
	};

	const getSelectPlafonTypeOptions = () => {
		if (!selectPlafonType.options?.length) return [];

		const NJFOptionOnly = [
			{
				label: PlafondType[1],
				value: PlafondType['Non Join Financing'],
				data: {
					id: PlafondType['Non Join Financing'],
					value: PlafondType[1],
				},
			},
		];

		const JFOptionOnly = [
			{
				label: PlafondType[2],
				value: PlafondType['Join Financing'],
				data: {
					id: PlafondType['Join Financing'],
					value: PlafondType[2],
				},
			},
		];

		const activeExistingPlafons = existingPlafons.filter(
			(plafon) => plafon.status === 'Aktif',
		);

		if (activeExistingPlafons.length > 0) {
			const hasExistingJF = activeExistingPlafons.find(
				(plafon) => plafon.financingLimitType === 'Join Financing',
			);
			const hasExistingNJF = activeExistingPlafons.find(
				(plafon) => plafon.financingLimitType === 'Non Join Financing',
			);
			if (hasExistingJF) {
				return NJFOptionOnly;
			} else if (hasExistingNJF) {
				return JFOptionOnly;
			} else {
				return selectPlafonType.options;
			}
		}

		if (isJfCustomer) {
			if (hasJF) {
				return NJFOptionOnly;
			}

			if (hasNJF) {
				return JFOptionOnly;
			}
		} else {
			return NJFOptionOnly;
		}

		return selectPlafonType.options;
	};

	if (queryCustomerDetail.isError) return <GenericErrorMessage />;

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={queryCustomerDetail.isLoading}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#customer-detail"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(customerId);
								}}
							>
								{customerInformation?.customerName}
							</a>,
						],
						['Customer ID', customerId],
					],
					[
						[
							'Kelas Industri',
							customerInformation?.industrySector.industryClass,
						],
						['Sumber Order', null],
					],
					[
						[
							'Portofolio Guideline',
							customerInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							customerInformation?.customerGroup?.id ? (
								<a
									href="#customer-detail"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										if (customerInformation?.customerGroup?.id) {
											openCustomerGroupDetail(
												customerInformation?.customerGroup?.id,
											);
										}
									}}
									aria-disabled={!customerInformation?.customerGroup?.id}
								>
									{customerInformation?.customerGroup?.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								key="view-dokumen"
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
				style={{
					marginBottom: 20,
				}}
			/>
			<FleetTitle>Add Prospect</FleetTitle>
			<FleetTitle>Informasi Limit Pembiayaan Existing</FleetTitle>
			<Table
				pagination={false}
				scroll={{ x: 'max-content' }}
				bordered
				loading={queryCustomerPlafons.isLoading}
				dataSource={
					existingPlafons.map((record, key) => {
						return {
							...record,
							key,
						};
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						dataIndex: 'no',
					},
					{
						title: 'No. MKL',
						dataIndex: 'napNo',
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							formatCurrency(record.existingFinancingLimit),
					},
					{
						title: 'Jenis Limit Pembiayaan',
						dataIndex: 'financingLimitType',
					},
					{
						title: 'Sifat Limit',
						dataIndex: 'financingLimitCharacteristic',
					},
					{
						title: 'Expired Date',
						render: (_, record) =>
							record.expiredDate
								? dayjs(record.expiredDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'NTF',
						render: (_, record) => formatCurrency(record.ntf) || 0,
					},
					{
						title: 'OS Principal',
						render: (_, record) =>
							record.outstandingPrincipal
								? formatCurrency(record.outstandingPrincipal)
								: '-',
					},
					{
						title: 'Sisa Limit',
						render: (_, record) =>
							record.outstandingFinancingLimit
								? formatCurrency(record.outstandingFinancingLimit)
								: '-',
					},
					{
						title: 'Status',
						dataIndex: 'status',
					},
					{
						title: 'Select',
						width: 1,
						align: 'center',
						render: (_, record) => {
							if (
								value?.data?.find(
									(plafon) =>
										plafon.companyPlafonId === record.customerPlafonId,
								) ||
								value?.data?.find(
									(plafon) =>
										PlafondType[plafon.plafonTypeId] ===
										record.financingLimitType,
								) ||
								record.status === 'Non Aktif' ||
								(!isJfCustomer &&
									record.financingLimitType === 'Join Financing')
								// || record.status === 'Non Aktif'
							) {
								return <ArrowRightOutlined style={{ color: '#bababa' }} />;
							}
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										setPlafonModal({ type: 'existing', data: record });
										form?.setFieldValue('form', {
											plafonTypeId:
												record.financingLimitType === 'Join Financing' ? 2 : 1,
											plafonTypeValue: record.financingLimitType,
											// orderTypeId: 2,
											// orderTypeValue: 'Penambahan Fasilitas',
											companyPlafonId: record.customerPlafonId,
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
			/>
			<Form.Provider>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={handleSubmitPlafon}
				>
					<FleetModal
						open={Boolean(plafonModal)}
						width={940}
						closeIcon={false}
						buttonPosition="end"
						onOk={() => {
							form.submit();
						}}
						onCancel={() => {
							setPlafonModal(null);
							form.resetFields([fieldName('form')]);
						}}
					>
						{plafonModal?.type === 'existing' ? (
							<Fragment>
								<FleetTitle>Update Existing Plafond</FleetTitle>
								{/* <p>{JSON.stringify(plafonModal.data)}</p> */}
								<Card
									style={{ marginBottom: 20, background: '#f8f8f8' }}
									size="small"
									title="Informasi Limit Pembiayaan Existing"
								>
									<DataRow
										labelCol={{
											span: 12,
										}}
										wrapperCol={{
											span: 12,
										}}
										items={[
											[
												['No. MKL', plafonModal.data?.napNo],
												[
													'Jenis Limit Pembiayaan',
													plafonModal.data?.financingLimitType,
												],
											],
											[
												[
													'Sifat Limit',
													plafonModal.data?.financingLimitCharacteristic,
												],
												[
													'Expired Date',
													dayjs(plafonModal.data?.expiredDate).format(
														'DD/MM/YYYY',
													),
												],
											],
											[
												[
													'Limit Pembiayaan Existing',
													formatCurrency(
														plafonModal.data?.existingFinancingLimit,
													),
												],
												['NTF', formatCurrency(plafonModal.data?.ntf)],
											],
											[
												[
													'OS Principal',
													formatCurrency(
														plafonModal.data?.outstandingPrincipal,
													),
												],
												[
													'Sisa Limit',
													formatCurrency(
														plafonModal.data?.outstandingFinancingLimit,
													),
												],
											],
											[['Status', plafonModal.data?.status]],
										]}
									/>
								</Card>

								<Form.Item
									name={fieldName(['form', 'companyPlafonId'])}
									noStyle
								/>

								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'segmentMarketValue'])}
											noStyle
										/>

										<Form.Item
											name={fieldName(['form', 'segmentMarketId'])}
											label="Segment Market"
											rules={[formRule.required()]}
											shouldUpdate
										>
											<Select
												{...selectSegmentMarket}
												options={getSelectSegmentMarket(
													plafonModal.data.financingLimitType!,
												)}
												disabled={!value?.form?.plafonTypeId}
												onSelect={(value, option) => {
													selectSegmentMarket.onSelect!(value, option);

													form.setFieldValue(
														fieldName(['form', 'segmentMarketValue']),
														option.data.value,
													);
													form.resetFields([
														fieldName(['form', 'orderSourceId']),
													]);
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
												}}
												onClear={() => {
													form.resetFields([
														fieldName(['form', 'orderSourceId']),
													]);
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
													form.resetFields([
														fieldName(['form', 'segmentMarketValue']),
													]);
												}}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'orderSourceId'])}
											label="Sumber Order"
											rules={[formRule.required()]}
											shouldUpdate
										>
											<Select
												{...selectSourceOrder}
												options={getSelectOrderSource()}
												disabled={!value?.form?.segmentMarketId}
												onSelect={(value, option) => {
													selectSourceOrder.onSelect!(value, option);
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
												}}
												onClear={() => {
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
												}}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										{value?.form?.segmentMarketId === SegmentMarket.Reguler ? (
											// <Form.Item
											// 	key="supplierDealer"
											// 	name={fieldName(['form', 'supplierDealer'])}
											// 	label="Nama Dealer/Supplier"
											// 	rules={[formRule.required()]}
											// >
											// 	<Input />
											// </Form.Item>
											<Form.Item
												key="supplierDealer"
												name={fieldName(['form', 'referrerUnit'])}
												label="Nama Dealer/Supplier"
												rules={[formRule.required()]}
												getValueFromEvent={(_, option) => option?.data}
												getValueProps={(value) => {
													if (!value) return {};
													return {
														value: {
															value: value.id,
															label: value.value,
														},
													};
												}}
											>
												<Select {...selectSupplierDealer} />
											</Form.Item>
										) : value?.form?.segmentMarketId &&
											value?.form.orderSourceId ? (
											// @ts-expect-error type doesn't exist
											{
												[SourceOrder['Captive JF Commercial']]: (
													<Form.Item
														key="Captive JF Commercial"
														name={fieldName(['form', 'referrerUnitId'])}
														label="Commercial Banking"
														rules={[formRule.required()]}
													>
														<Select
															{...selectCommercialBank}
															options={
																selectCommercialBank.options?.length
																	? selectCommercialBank.options
																	: []
															}
															onSelect={(value, option) => {
																selectCommercialBank.onSelect?.(value, option);
																form.setFieldValue(
																	['form', 'referrerUnit'],
																	option.data,
																);
															}}
														/>
													</Form.Item>
												),
												[SourceOrder['Captive JF Corporate']]: (
													<Form.Item
														key="Captive JF Corporate"
														name={fieldName(['form', 'referrerUnitId'])}
														label="Department"
														rules={[formRule.required()]}
													>
														<Select
															{...selectCorporateBank}
															options={
																selectCorporateBank.options?.length
																	? selectCorporateBank.options
																	: []
															}
															onSelect={(value, option) => {
																selectCorporateBank.onSelect?.(value, option);
																form.setFieldValue(
																	['form', 'referrerUnit'],
																	option.data,
																);
															}}
														/>
													</Form.Item>
												),
												[SourceOrder['Captive Non JF']]: (
													<Form.Item
														key="Captive Non JF"
														name={fieldName(['form', 'referrerUnit'])}
														label="BMRI Branch/Area"
														rules={[formRule.required()]}
														getValueFromEvent={(_, option) => {
															return {
																id: option?.data.branchId,
																value: option?.data.branchName,
															};
														}}
														getValueProps={(value) => {
															if (!value) return {};
															return {
																value: {
																	value: value.id,
																	label: value.value,
																},
															};
														}}
													>
														<Select {...selectMandiriBranches} />
													</Form.Item>
												),
											}[
											value.form
												.orderSourceId as unknown as keyof typeof SourceOrder
											] || null
										) : null}
									</Col>
								</Row>
								<Form.Item
									name={['form', 'referrerUnit']}
									noStyle
								/>
								{(value?.form?.orderSourceId ===
									SourceOrder['Captive JF Commercial'] ||
									value?.form?.orderSourceId ===
									SourceOrder['Captive JF Corporate']) &&
									value.form.referrerUnitId && (
										<Fragment>
											<Divider />
											<Row gutter={[40, 20]}>
												<Col span={12}>
													<Form.Item label="Group">
														<Input
															disabled
															value={
																form.getFieldValue(['form', 'referrerUnit'])
																	?.group || '-'
															}
														/>
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item label="BUC">
														<Input
															disabled
															value={
																form.getFieldValue(['form', 'referrerUnit'])
																	?.buc || '-'
															}
														/>
													</Form.Item>
												</Col>
											</Row>
										</Fragment>
									)}
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'referrerName'])}
											label="Nama Pemberi Refferal"
											rules={[formRule.required()]}
										>
											<Input />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'referrerPositionId'])}
											label="Jabatan Pemberi Refferal"
											rules={[formRule.required()]}
										>
											<Select
												{...selectGeneralPosition}
												options={
													selectGeneralPosition.options?.length
														? selectGeneralPosition.options
														: []
												}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'referrerPhoneNumber'])}
											label="Nomor Telepon"
											wrapperCol={{
												span: 12,
												style: { whiteSpace: 'nowrap' },
											}}
											validateTrigger={['onBlur', 'onFocus', 'onChange']}
											rules={[
												formRule.required(),
												formRule.strNumber(),
												formRule.phoneNumber(),
											]}
										>
											<Input maxLength={13} />
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										{value?.form?.orderTypeId ===
											OrderTypeExisting['Perpanjangan Fasilitas'] ? (
											<Form.Item
												name={fieldName(['form', 'applicationLimit'])}
												label="Pengajuan Limit"
												initialValue={0}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										) : (
											<Form.Item
												name={fieldName(['form', 'applicationLimit'])}
												label="Pengajuan Limit"
												rules={[formRule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										)}
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'financingLimitTypeId'])}
											label="Sifat Limit"
											rules={[formRule.required()]}
										>
											<Select
												{...selectFinancingLimitCharacteristic}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'plafonTypeValue'])}
											noStyle
										/>
										<Form.Item
											name={fieldName(['form', 'plafonTypeId'])}
											label="Jenis Plafon"
											rules={[formRule.required()]}
										>
											<Select
												{...selectPlafonType}
												options={getSelectExistingPlafonTypeOptions(
													plafonModal.data.financingLimitType!,
												)}
												disabled
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'orderTypeValue'])}
											noStyle
										/>
										<Form.Item
											name={fieldName(['form', 'orderTypeId'])}
											label="Tujuan Pembiayaan"
											rules={[formRule.required()]}
										>
											<Select
												options={getEnumMapping(OrderTypeExisting)}
												// disabled
												onSelect={(value) => {
													form.setFieldValue(
														fieldName(['form', 'orderTypeValue']),
														OrderTypeExisting[value],
													);
													form.setFieldValue(
														fieldName(['form', 'applicationLimit']),
														undefined,
													);
												}}
												onClear={() => {
													form.resetFields([
														fieldName(['form', 'orderTypeValue']),
													]);
													form.setFieldValue(
														fieldName(['form', 'applicationLimit']),
														undefined,
													);
												}}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row>
									<Col span={24}>
										<Form.Item
											name={fieldName(['form', 'financingTypeIds'])}
											label="Jenis Pembiayaan"
											rules={[formRule.required()]}
											labelCol={{ span: 6 }}
											wrapperCol={{
												span: 18,
												style: { marginLeft: -10 },
											}}
										>
											<Checkbox.Group style={{ display: 'flex' }}>
												<Row>
													{queryFinancingType.data?.map(({ id, value: label }) => (
														<Col key={id} span={12}>
															<Checkbox value={id} style={{ lineHeight: 2 }}>
																{label}
															</Checkbox>
														</Col>
													))}
												</Row>
											</Checkbox.Group>
										</Form.Item>
									</Col>
								</Row>

								{financingTypeDetailItems.length > 0 && (
									<Fragment>
										<Divider />
										<Row>
											<Col span={24}>
												<Form.Item
													name={fieldName(['form', 'financingTypeDetailIds'])}
													label="Jenis Fasilitas"
													rules={[formRule.required()]}
													labelCol={{ span: 6 }}
													wrapperCol={{
														span: 18,
														style: { marginLeft: -10 },
													}}
												>
													<Checkbox.Group style={{ display: 'flex' }}>
														<Row>
															{financingTypeDetailItems.map((item) => (
																<Col key={item.data.id} span={12}>
																	<Checkbox value={item.data.id} style={{ lineHeight: 2 }}>
																		{item.data.value}
																	</Checkbox>
																</Col>
															))}
														</Row>
													</Checkbox.Group>
												</Form.Item>
											</Col>
										</Row>
									</Fragment>
								)}

								<Divider />
								<Row>
									<Col span={24}>
										<Form.Item
											name={fieldName(['form', 'financingObjectIds'])}
											label="Object Pembiayaan"
											rules={[formRule.required()]}
											labelCol={{
												span: 6,
											}}
											wrapperCol={{
												span: 18,
												style: {
													marginLeft: -10,
												},
											}}
										>
											<Checkbox.Group style={{ display: 'flex' }}>
												<Row>
													{queryFinancingObject.data?.map(({ id, value }) => (
														<Col
															key={id}
															span={6}
														>
															<Checkbox
																value={id}
																style={{ lineHeight: 2 }}
															>
																{value}
															</Checkbox>
														</Col>
													))}
												</Row>
											</Checkbox.Group>
										</Form.Item>
									</Col>
								</Row>
							</Fragment>
						) : (
							<Fragment>
								<FleetTitle>Add Plafond</FleetTitle>
								{isJfCustomer && (
									<Alert
										type="info"
										message="Silakan pilih Jenis Plafon JF/NJF terlebih dahulu"
										showIcon
										style={{ marginBottom: 20 }}
									/>
								)}

								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'segmentMarketValue'])}
											noStyle
										/>

										<Form.Item
											name={fieldName(['form', 'segmentMarketId'])}
											label="Segment Market"
											rules={[formRule.required()]}
											shouldUpdate
										>
											<Select
												{...selectSegmentMarket}
												options={getSelectSegmentMarket(
													value?.form?.plafonTypeValue ?? '',
												)}
												// disabled={hasJF && hasNJF}
												disabled={!value?.form?.plafonTypeId}
												onSelect={(value, option) => {
													selectSegmentMarket.onSelect!(value, option);
													form.resetFields([
														fieldName(['form', 'orderSourceId']),
														fieldName(['form', 'referrerUnitId']),
														// fieldName(['form', 'plafonTypeId']),
													]);
													form.setFieldValue(
														fieldName(['form', 'segmentMarketValue']),
														option.data.value,
													);
												}}
												onClear={() => {
													form.resetFields([
														fieldName(['form', 'orderSourceId']),
													]);
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
													form.resetFields([
														fieldName(['form', 'segmentMarketValue']),
													]);
												}}
											/>
										</Form.Item>
									</Col>

								</Row>
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'orderSourceId'])}
											label="Sumber Order"
											rules={[formRule.required()]}
											shouldUpdate
										>
											<Select
												{...selectSourceOrder}
												options={getSelectOrderSource()}
												disabled={!value?.form?.segmentMarketId}
												onSelect={(value, option) => {
													selectSourceOrder.onSelect!(value, option);
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
														// fieldName(['form', 'plafonTypeId']),
														// fieldName(['form', 'plafonTypeValue']),
													]);
												}}
												onClear={() => {
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
												}}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										{value?.form?.segmentMarketId === SegmentMarket.Reguler ? (
											<Form.Item
												key="supplierDealer"
												name={fieldName(['form', 'referrerUnit'])}
												label="Nama Dealer/Supplier"
												rules={[formRule.required()]}
												getValueFromEvent={(_, option) => option?.data}
												getValueProps={(value) => {
													if (!value) return {};
													return {
														value: {
															value: value.id,
															label: value.value,
														},
													};
												}}
											>
												<Select {...selectSupplierDealer} />
											</Form.Item>
										) : value?.form?.segmentMarketId &&
											value?.form.orderSourceId ? (
											// @ts-expect-error type doesn't exist
											{
												[SourceOrder['Captive JF Commercial']]: (
													<Form.Item
														key="Captive JF Commercial"
														name={fieldName(['form', 'referrerUnitId'])}
														label="Commercial Banking"
														rules={[formRule.required()]}
													>
														<Select
															{...selectCommercialBank}
															options={
																selectCommercialBank.options?.length
																	? selectCommercialBank.options
																	: []
															}
															onSelect={(value, option) => {
																selectCommercialBank.onSelect?.(value, option);
																form.setFieldValue(
																	['form', 'referrerUnit'],
																	option.data,
																);
															}}
														/>
													</Form.Item>
												),
												[SourceOrder['Captive JF Corporate']]: (
													<Form.Item
														key="Captive JF Corporate"
														name={fieldName(['form', 'referrerUnitId'])}
														label="Department"
														rules={[formRule.required()]}
													>
														<Select
															{...selectCorporateBank}
															options={
																selectCorporateBank.options?.length
																	? selectCorporateBank.options
																	: []
															}
															onSelect={(value, option) => {
																selectCorporateBank.onSelect?.(value, option);
																form.setFieldValue(
																	['form', 'referrerUnit'],
																	option.data,
																);
															}}
														/>
													</Form.Item>
												),
												[SourceOrder['Captive Non JF']]: (
													<Form.Item
														key="Captive Non JF"
														name={fieldName(['form', 'referrerUnit'])}
														label="BMRI Branch/Area"
														rules={[formRule.required()]}
														getValueFromEvent={(_, option) => {
															return {
																id: option?.data.branchId,
																value: option?.data.branchName,
															};
														}}
														getValueProps={(value) => {
															if (!value) return {};
															return {
																value: {
																	value: value.id,
																	label: value.value,
																},
															};
														}}
													>
														<Select {...selectMandiriBranches} />
													</Form.Item>
												),
											}[
											value.form
												.orderSourceId as unknown as keyof typeof SourceOrder
											] || null
										) : null}
									</Col>
								</Row>
								<Form.Item
									name={['form', 'referrerUnit']}
									noStyle
								/>
								{(value?.form?.orderSourceId ===
									SourceOrder['Captive JF Commercial'] ||
									value?.form?.orderSourceId ===
									SourceOrder['Captive JF Corporate']) &&
									value.form.referrerUnitId && (
										<Fragment>
											<Divider />
											<Row gutter={[40, 20]}>
												<Col span={12}>
													<Form.Item label="Group">
														<Input
															disabled
															value={
																form.getFieldValue(['form', 'referrerUnit'])
																	?.group || '-'
															}
														/>
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item label="BUC">
														<Input
															disabled
															value={
																form.getFieldValue(['form', 'referrerUnit'])
																	?.buc || '-'
															}
														/>
													</Form.Item>
												</Col>
											</Row>
										</Fragment>
									)}
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'referrerName'])}
											label="Nama Pemberi Refferal"
											rules={[formRule.required()]}
										>
											<Input />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'referrerPositionId'])}
											label="Jabatan Pemberi Refferal"
											rules={[formRule.required()]}
										>
											<Select
												{...selectGeneralPosition}
												options={
													selectGeneralPosition.options?.length
														? selectGeneralPosition.options
														: []
												}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'referrerPhoneNumber'])}
											label="Nomor Telepon"
											wrapperCol={{
												span: 12,
												style: { whiteSpace: 'nowrap' },
											}}
											validateTrigger={['onBlur', 'onFocus', 'onChange']}
											rules={[
												formRule.required(),
												formRule.strNumber(),
												formRule.phoneNumber(),
											]}
										>
											<Input maxLength={13} />
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'applicationLimit'])}
											label="Pengajuan Limit"
											rules={[formRule.required()]}
										>
											<InputNumber {...inputCurrency} />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'financingLimitTypeId'])}
											label="Sifat Limit"
											rules={[formRule.required()]}
										>
											<Select
												{...selectFinancingLimitCharacteristic}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />

								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'plafonTypeValue'])}
											noStyle
										/>
										<Form.Item
											name={fieldName(['form', 'plafonTypeId'])}
											label="Jenis Plafon"
											rules={[formRule.required()]}
											shouldUpdate
										>
											<Select
												{...selectPlafonType}
												options={getSelectPlafonTypeOptions()}
												onSelect={(value, option) => {
													selectPlafonType.onSelect!(value, option);
													form.setFieldValue(
														fieldName(['form', 'plafonTypeValue']),
														option.data.value,
													);
													form.resetFields([
														fieldName(['form', 'segmentMarketId']),
													]);
													form.resetFields([
														fieldName(['form', 'orderSourceId']),
													]);
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
												}}
												onClear={() => {
													form.resetFields([
														fieldName(['form', 'plafonTypeValue']),
													]);
													form.resetFields([
														fieldName(['form', 'segmentMarketId']),
													]);
													form.resetFields([
														fieldName(['form', 'orderSourceId']),
													]);
													form.resetFields([
														fieldName(['form', 'referrerUnitId']),
													]);
												}}
											/>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item
											name={fieldName(['form', 'orderTypeValue'])}
											noStyle
										/>
										<Form.Item
											name={fieldName(['form', 'orderTypeId'])}
											label="Tujuan Pembiayaan"
											rules={[formRule.required()]}
										>
											<Select
												allowClear
												options={getEnumMapping(OrderType)}
												onSelect={(value) => {
													form.setFieldValue(
														fieldName(['form', 'orderTypeValue']),
														OrderType[value],
													);
												}}
												onClear={() => {
													form.resetFields([
														fieldName(['form', 'orderTypeValue']),
													]);
												}}
											/>
										</Form.Item>
									</Col>

								</Row>
								<Divider />
								<Row>
									<Col span={24}>
										<Form.Item
											name={fieldName(['form', 'financingTypeIds'])}
											label="Jenis Pembiayaan"
											rules={[formRule.required()]}
											labelCol={{ span: 6 }}
											wrapperCol={{
												span: 18,
												style: { marginLeft: -10 },
											}}
										>
											<Checkbox.Group style={{ display: 'flex' }}>
												<Row>
													{queryFinancingType.data?.map(({ id, value: label }) => (
														<Col key={id} span={12}>
															<Checkbox value={id} style={{ lineHeight: 2 }}>
																{label}
															</Checkbox>
														</Col>
													))}
												</Row>
											</Checkbox.Group>
										</Form.Item>
									</Col>
								</Row>

								{financingTypeDetailItems.length > 0 && (
									<Fragment>
										<Divider />
										<Row>
											<Col span={24}>
												<Form.Item
													name={fieldName(['form', 'financingTypeDetailIds'])}
													label="Jenis Fasilitas"
													rules={[formRule.required()]}
													labelCol={{ span: 6 }}
													wrapperCol={{
														span: 18,
														style: { marginLeft: -10 },
													}}
												>
													<Checkbox.Group style={{ display: 'flex' }}>
														<Row>
															{financingTypeDetailItems.map((item) => (
																<Col key={item.data.id} span={12}>
																	<Checkbox value={item.data.id} style={{ lineHeight: 2 }}>
																		{item.data.value}
																	</Checkbox>
																</Col>
															))}
														</Row>
													</Checkbox.Group>
												</Form.Item>
											</Col>
										</Row>
									</Fragment>
								)}
								<Divider />
								<Row>
									<Col span={24}>
										<Form.Item
											name={fieldName(['form', 'financingObjectIds'])}
											label="Object Pembiayaan"
											rules={[formRule.required()]}
											labelCol={{
												span: 6,
											}}
											wrapperCol={{
												span: 18,
												style: {
													marginLeft: -10,
												},
											}}
										>
											<Checkbox.Group style={{ display: 'flex' }}>
												<Row>
													{queryFinancingObject.data?.map(({ id, value }) => (
														<Col
															key={id}
															span={6}
														>
															<Checkbox
																value={id}
																style={{ lineHeight: '45px' }}
															>
																{value}
															</Checkbox>
														</Col>
													))}
												</Row>
											</Checkbox.Group>
										</Form.Item>
									</Col>
								</Row>
							</Fragment>
						)}
						<div style={{ height: 50 }} />
					</FleetModal>

					<Form.Item
						name="data"
						noStyle
					/>

					<Form.Item shouldUpdate>
						{({ getFieldValue }) => {
							const dataSource =
								(getFieldValue('data') as ProspectPlafonData[]) ?? [];

							const activeExistingPlafons = existingPlafons.filter(
								(plafon) => plafon.status === 'Aktif',
							);

							return (
								<Fragment>
									{dataSource.length > (isJfCustomer ? 1 : 0) ? (
										<Button
											disabled
											style={{
												width: 200,
												display: 'block',
												marginLeft: 'auto',
												marginTop: 50,
												marginBottom: 30,
											}}
										>
											ADD PLAFON
										</Button>
									) : (
										<Button
											disabled={activeExistingPlafons.length >= 2}
											style={{
												width: 200,
												display: 'block',
												marginLeft: 'auto',
												marginTop: 50,
												marginBottom: 30,
											}}
											onClick={() => {
												setPlafonModal({ type: 'add' });
											}}
										>
											ADD PLAFON
										</Button>
									)}

								</Fragment>
							);
						}}
					</Form.Item>
				</Form>
			</Form.Provider>

			<Collapse
				defaultActiveKey={['debitur']}
				style={{ marginTop: 24 }}
				items={[
					{
						key: 'debitur',
						label: 'Ringkasan & Total Pengajuan Debitur',
						children: (
							<Fragment>
								<FleetTitle>
									Informasi Limit Pengajuan Pembiayaan
								</FleetTitle>
								<Table
									scroll={{ x: 'max-content' }}
									pagination={false}
									bordered
									loading={queryPlafonSummary.isLoading}
									dataSource={(
										queryPlafonSummary.data?.data
											?.requestedFinancingLimits ?? []
									).map((item, key) => ({ ...item, key }))}
									columns={[
										{
											title: 'No',
											render: (_, __, index) => index + 1,
										},
										{
											title: 'No. MKL',
											render: (_, record) => record.mklNo ?? '-',
										},
										{
											title: 'Limit Pembiayaan',
											render: (_, record) =>
												formatCurrency(record.financingLimitAmount ?? undefined),
										},
										{
											title: 'Sifat Limit',
											render: (_, record) =>
												record.financingLimitType ?? '-',
										},
										{
											title: 'Jenis Limit Pembiayaan',
											render: (_, record) =>
												record.plafonType ?? '-',
										},
										{
											title: 'Expired Date',
											render: (_, record) =>
												record.expiredDate
													? dayjs(record.expiredDate).format(
														'DD/MM/YYYY',
													)
													: '-',
										},
										{
											title: 'NTF',
											render: (_, record) =>
												formatCurrency(record.ntfAmount ?? undefined) ?? 0,
										},
										{
											title: 'OS Principal',
											render: (_, record) =>
												formatCurrency(
													record.outstandingPrincipalAmount ?? undefined,
												),
										},
										{
											title: 'Sisa Limit',
											render: (_, record) =>
												formatCurrency(record.outstandingLimitAmount ?? undefined),
										},
									]}
								/>

								<FleetTitle style={{ marginTop: 24 }}>
									Ringkasan Existing & Pengajuan
								</FleetTitle>
								{(() => {
									const details: (SummaryFinancingLimitDetail & {
										key: number;
									})[] = (
										queryPlafonSummary.data?.data
											?.summaryFinancingLimit?.details ?? []
									).map((item, key) => ({ ...item, key }));

									const sifatRowSpans = details.map((_, i) => {
										if (
											i === 0 ||
											details[i].financingLimitType !==
											details[i - 1].financingLimitType
										) {
											let count = 1;
											let j = i + 1;
											while (
												j < details.length &&
												details[j].financingLimitType ===
												details[i].financingLimitType
											) {
												count++;
												j++;
											}
											return count;
										}
										return 0;
									});

									const jenisRowSpans = details.map((_, i) => {
										if (
											i === 0 ||
											details[i].financingLimitType !==
											details[i - 1].financingLimitType ||
											details[i].plafonType !==
											details[i - 1].plafonType
										) {
											let count = 1;
											let j = i + 1;
											while (
												j < details.length &&
												details[j].financingLimitType ===
												details[i].financingLimitType &&
												details[j].plafonType ===
												details[i].plafonType
											) {
												count++;
												j++;
											}
											return count;
										}
										return 0;
									});

									const { summaryFinancingLimit } =
										queryPlafonSummary.data?.data ?? {};

									return (
										<Table
											scroll={{ x: 'max-content' }}
											pagination={false}
											bordered
											loading={queryPlafonSummary.isLoading}
											dataSource={details}
											summary={() => {
												if (!details.length) return null;
												return (
													<Table.Summary.Row>
														<Table.Summary.Cell
															index={0}
															colSpan={5}
														>
															<strong>Grand Total</strong>
														</Table.Summary.Cell>
														<Table.Summary.Cell index={5}>
															<strong>
																{formatCurrency(
																	summaryFinancingLimit?.totalNtfAmount ?? undefined,
																)}
															</strong>
														</Table.Summary.Cell>
														<Table.Summary.Cell index={6}>
															<strong>
																{formatCurrency(
																	summaryFinancingLimit?.totalOutstandingPrincipalAmount ?? undefined,
																)}
															</strong>
														</Table.Summary.Cell>
														<Table.Summary.Cell index={7}>
															<strong>
																{formatCurrency(
																	summaryFinancingLimit?.totalOutstandingLimitAmount ?? undefined,
																)}
															</strong>
														</Table.Summary.Cell>
														<Table.Summary.Cell index={8}>
															<strong>
																{formatCurrency(
																	summaryFinancingLimit?.totalObligorAmount ?? undefined,
																)}
															</strong>
														</Table.Summary.Cell>
													</Table.Summary.Row>
												);
											}}
											columns={[
												{
													title: 'No',
													onCell: (_, index) => ({
														rowSpan: sifatRowSpans[index!],
													}),
													render: (_, __, index) => {
														let no = 0;
														for (let i = 0; i <= index; i++) {
															if (sifatRowSpans[i] > 0) no++;
														}
														return no;
													},
												},
												{
													title: 'Sifat Limit',
													onCell: (_, index) => ({
														rowSpan: sifatRowSpans[index!],
													}),
													render: (_, record) =>
														record.financingLimitType ?? '-',
												},
												{
													title: 'Jenis Limit',
													onCell: (_, index) => ({
														rowSpan: jenisRowSpans[index!],
													}),
													render: (_, record) =>
														record.plafonType ?? '-',
												},
												{
													title: 'Status',
													dataIndex: 'status',
												},
												{
													title: 'Limit Pembiayaan',
													render: (_, record) =>
														formatCurrency(
															record.financingLimitAmount ?? undefined,
														),
												},
												{
													title: 'NTF',
													render: (_, record) =>
														formatCurrency(record.ntfAmount ?? undefined),
												},
												{
													title: 'Outstanding Principal',
													render: (_, record) =>
														formatCurrency(
															record.outstandingPrincipalAmount ?? undefined,
														),
												},
												{
													title: 'Sisa Limit',
													render: (_, record) =>
														formatCurrency(
															record.outstandingLimitAmount ?? undefined,
														),
												},
												{
													title: 'Obligor',
													render: (_, record) =>
														formatCurrency(record.obligorAmount ?? undefined),
												},
											]}
										/>
									);
								})()}
							</Fragment>
						),
					},
				]}
			/>

			<Collapse
				defaultActiveKey={['group']}
				style={{ marginTop: 16 }}
				items={[
					{
						key: 'group',
						label: 'Ringkasan & Total Pengajuan Eksisting Group Usaha',
						children: (
							<Fragment>
								<FleetTitle>Informasi Limit Group Usaha</FleetTitle>
								{(() => {
									const details: (SummaryGroupPlafonDetail & {
										key: number;
									})[] = (
										queryPlafonSummary.data?.data
											?.summaryGroupPlafon?.details ?? []
									).map((item, key) => ({ ...item, key }));

									const customerRowSpans = details.map((_, i) => {
										if (
											i === 0 ||
											details[i].customerId !==
											details[i - 1].customerId
										) {
											let count = 1;
											let j = i + 1;
											while (
												j < details.length &&
												details[j].customerId ===
												details[i].customerId
											) {
												count++;
												j++;
											}
											return count;
										}
										return 0;
									});

									let customerCounter = 0;
									const customerNoMap: Record<string, number> = {};
									details.forEach((detail) => {
										if (
											detail.customerId &&
											!customerNoMap[detail.customerId]
										) {
											customerNoMap[detail.customerId] =
												++customerCounter;
										}
									});

									const { summaryGroupPlafon, groupBmpp } =
										queryPlafonSummary.data?.data ?? {};

									return (
										<Fragment>
											<Table
												scroll={{ x: 'max-content' }}
												pagination={false}
												bordered
												loading={queryPlafonSummary.isLoading}
												dataSource={details}
												summary={() => {
													if (!details.length) return null;
													return (
														<Table.Summary.Row>
															<Table.Summary.Cell
																index={0}
																colSpan={4}
															>
																<strong>Grand Total</strong>
															</Table.Summary.Cell>
															<Table.Summary.Cell index={4}>
																<strong>
																	{formatCurrency(
																		summaryGroupPlafon?.totalNtfAmount ?? undefined,
																	)}
																</strong>
															</Table.Summary.Cell>
															<Table.Summary.Cell index={5}>
																<strong>
																	{formatCurrency(
																		summaryGroupPlafon?.totalOutstandingPrincipalAmount ?? undefined,
																	)}
																</strong>
															</Table.Summary.Cell>
															<Table.Summary.Cell index={6}>
																<strong>
																	{formatCurrency(
																		summaryGroupPlafon?.totalOutstandingLimitAmount ?? undefined,
																	)}
																</strong>
															</Table.Summary.Cell>
															<Table.Summary.Cell index={7}>
																<strong>
																	{formatCurrency(
																		summaryGroupPlafon?.totalObligorAmount ?? undefined,
																	)}
																</strong>
															</Table.Summary.Cell>
														</Table.Summary.Row>
													);
												}}
												columns={[
													{
														title: 'No',
														onCell: (_, index) => ({
															rowSpan: customerRowSpans[index!],
														}),
														render: (_, record) =>
															record.customerId
																? customerNoMap[record.customerId]
																: '',
													},
													{
														title: 'Nama Customer',
														onCell: (_, index) => ({
															rowSpan: customerRowSpans[index!],
														}),
														render: (_, record) =>
															record.customerName ?? '-',
													},
													{
														title: 'Status',
														dataIndex: 'status',
													},
													{
														title: 'Limit Pembiayaan',
														render: (_, record) =>
															formatCurrency(
																record.financingLimitAmount,
															),
													},
													{
														title: 'NTF',
														render: (_, record) =>
															formatCurrency(record.ntfAmount ?? undefined),
													},
													{
														title: 'Outstanding Principal',
														render: (_, record) =>
															formatCurrency(
																record.outstandingPrincipalAmount,
															),
													},
													{
														title: 'Sisa Limit',
														render: (_, record) =>
															formatCurrency(
																record.outstandingLimitAmount,
															) ?? '-',
													},
													{
														title: 'Obligor',
														render: (_, record) =>
															formatCurrency(record.obligorAmount ?? undefined),
													},
												]}
											/>

											{groupBmpp && (
												<Table
													style={{ marginTop: 16 }}
													scroll={{ x: 'max-content' }}
													pagination={false}
													bordered
													dataSource={[
														{
															key: 0,
															groupName: groupBmpp,
															totalOutstandingPrincipal:
																queryPlafonSummary.data
																	?.data
																	?.totalOutstandingLimitAmount,
															totalObligor:
																queryPlafonSummary.data
																	?.data?.totalObligorAmount,
														},
													]}
													columns={[
														{
															title: 'Nama Group Usaha',
															dataIndex: 'groupName',
															onHeaderCell: () => ({
																style: {
																	background: '#1557BC',
																	color: '#fff',
																},
															}),
														},
														{
															title: 'Total Outstanding Principal',
															render: (_, record) =>
																formatCurrency(
																	record.totalOutstandingPrincipal ?? undefined,
																),
															onHeaderCell: () => ({
																style: {
																	background: '#1557BC',
																	color: '#fff',
																},
															}),
														},
														{
															title: 'Total Obligor',
															render: (_, record) =>
																formatCurrency(record.totalObligor ?? undefined),
															onHeaderCell: () => ({
																style: {
																	background: '#1557BC',
																	color: '#fff',
																},
															}),
														},
													]}
												/>
											)}
										</Fragment>
									);
								})()}
							</Fragment>
						),
					},
				]}
			/>

			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 50,
					marginBottom: 32,
				}}
				loading={prospectMutation.isPending}
				onClick={handleSubmitProspect}
			>
				SAVE
			</Button>

			<FleetModal
				open={Boolean(successModal)}
				cancelText="BACK"
				okText="NEXT"
				onOk={() => {
					setSuccessModal('');
					navigate('FRMCustomerCheckingPage', { prospectId: successModal });
				}}
				onCancel={() => {
					navigate('FRMProspectListPage');
				}}
			>
				<Result
					status="success"
					title={
						<Fragment>
							<small>Prospect ID Anda</small>
							<div style={{ fontSize: 30 }}>{successModal}</div>
						</Fragment>
					}
				/>
			</FleetModal>
		</Fragment>
	);
};

export default ProspectPage;
